import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ChangePasswordModal from '../components/ChangePasswordModal';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  username: string;
  role: 'admin' | 'manager' | 'freelancer' | 'referrer' | 'client';
  is_approved: boolean;
  whatsapp_contact: string;
  account_number: string;
  bank_name: string;
  account_name: string;
  referrer_id?: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const loadProfile = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await axios.get('http://localhost:5000/api/users/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setProfile(response.data.data.user);
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      } else {
        setMessage('Error loading profile');
      }
    } catch (error: any) {
      console.error('Error loading profile:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/signin');
      } else {
        setMessage(error.response?.data?.message || 'Error loading profile');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'manager': return 'role-manager';
      case 'freelancer': return 'role-freelancer';
      case 'referrer': return 'role-referrer';
      case 'client': return 'role-client';
      default: return 'role-admin';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile) return;
    
    const { name, value } = e.target;
    setProfile({
      ...profile,
      [name]: value
    });
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    setIsSaving(true);
    setMessage('');

    try {
      const response = await axios.put(
        'http://localhost:5000/api/users/profile',
        profile,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Update both state and localStorage with fresh data from server
        const updatedUser = response.data.data.user;
        setProfile(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setMessage('Profile updated successfully!');
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setMessage(error.response?.data?.message || 'Error saving profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = () => {
    setIsPasswordModalOpen(true);
  };

  const handlePasswordChangeSuccess = () => {
    setMessage('Password changed successfully!');
    loadProfile(); // Refresh profile data
  };

  const showFinancialSection = profile?.role === 'freelancer' || profile?.role === 'referrer';
  const hasFinancialInfo = profile?.account_number && profile?.bank_name && profile?.account_name;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Error loading profile</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary mt-4"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-6">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                className={`nav-link ${location.pathname === '/dashboard' ? 'nav-link-active' : ''}`}
                title="Dashboard"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/profile')}
                className={`nav-link ${location.pathname === '/profile' ? 'nav-link-active' : ''}`}
                title="Profile"
              >
                Profile
              </button>
              {(profile.role === 'admin' || profile.role === 'manager') && (
                <button 
                  type="button"
                  onClick={() => navigate(profile.role === 'admin' ? '/admin' : '/manager')}
                  className={`nav-link ${(location.pathname === '/admin' || location.pathname === '/manager') ? 'nav-link-active' : ''}`}
                  title={profile.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                >
                  {profile.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Info - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{profile.username}</div>
                <div className="flex items-center gap-2">
                  <span className={`role-badge ${getRoleBadgeColor(profile.role)}`}>
                    {profile.role}
                  </span>
                  <span className={`status-badge ${profile.is_approved ? 'approved' : 'pending'}`}>
                    {profile.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              type="button"
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={toggleMobileMenu}
              title="Toggle menu"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logout Button - Desktop */}
            <button 
              type="button"
              onClick={handleLogout}
              className="hidden md:flex btn btn-ghost btn-sm"
              title="Logout"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <button 
                type="button"
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/dashboard' ? 'nav-link-mobile-active' : ''}`}
                title="Dashboard"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Dashboard
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/profile' ? 'nav-link-mobile-active' : ''}`}
                title="Profile"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile
              </button>

              {(profile.role === 'admin' || profile.role === 'manager') && (
                <button 
                  type="button"
                  onClick={() => {
                    navigate(profile.role === 'admin' ? '/admin' : '/manager');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`nav-link-mobile ${(location.pathname === '/admin' || location.pathname === '/manager') ? 'nav-link-mobile-active' : ''}`}
                  title={profile.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                >
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {profile.role === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                </button>
              )}

              {/* Mobile User Info */}
              <div className="px-3 py-2 border-t border-gray-200 mt-3 pt-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{profile.username}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`role-badge ${getRoleBadgeColor(profile.role)}`}>
                        {profile.role}
                      </span>
                      <span className={`status-badge ${profile.is_approved ? 'approved' : 'pending'}`}>
                        {profile.is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Logout */}
              <button 
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="nav-link-mobile text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Logout"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Profile Content */}
      <div className="profile-container">
        <div className="profile-content">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Management</h1>
                <p className="text-gray-600">Manage your account information and preferences</p>
              </div>
              <button 
                type="button"
                onClick={() => navigate('/dashboard')} 
                className="btn btn-secondary"
                title="Back to Dashboard"
              >
                ⬅ Back to Dashboard
              </button>
            </div>
          </div>

          {message && (
            <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'} mb-6`}>
              {message}
            </div>
          )}

          <div className="space-y-6">
            {/* Personal Information Card */}
            <div className="profile-card">
              <div className="card-header">
                <h2 className="card-title">Personal Information</h2>
                {!isEditing && (
                  <button 
                    type="button"
                    onClick={() => setIsEditing(true)} 
                    className="btn btn-primary btn-sm"
                    title="Edit Profile"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Profile
                  </button>
                )}
              </div>

              <div className="profile-fields">
                {/* Read-only fields */}
                <div className="form-group">
                  <label htmlFor="user_id" className="form-label">User ID</label>
                  <input 
                    type="text" 
                    id="user_id"
                    value={profile.id} 
                    disabled 
                    className="form-input readonly-field"
                    title="User ID"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role" className="form-label">Role</label>
                  <input 
                    type="text" 
                    id="role"
                    value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)} 
                    disabled 
                    className="form-input readonly-field"
                    title="Role"
                  />
                </div>

                {profile.referrer_id && (
                  <div className="form-group">
                    <label htmlFor="referrer_id" className="form-label">Referrer ID</label>
                    <input 
                      type="text" 
                      id="referrer_id"
                      value={profile.referrer_id} 
                      disabled 
                      className="form-input readonly-field"
                      title="Referrer ID"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="status" className="form-label">Account Status</label>
                  <input 
                    type="text" 
                    id="status"
                    value={profile.is_approved ? 'Approved ✅' : 'Pending Approval ⏳'} 
                    disabled 
                    className="form-input readonly-field"
                    title="Account Status"
                  />
                </div>

                {/* Editable fields */}
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={profile.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    required
                    title="Full Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profile.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    required
                    title="Email Address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="username" className="form-label">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={profile.username}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    required
                    title="Username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="whatsapp_contact" className="form-label">WhatsApp Contact *</label>
                  <input
                    type="tel"
                    id="whatsapp_contact"
                    name="whatsapp_contact"
                    value={profile.whatsapp_contact}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="form-input"
                    placeholder="+1234567890"
                    required
                    title="WhatsApp Contact"
                  />
                  <div className="form-hint">
                    Include country code (e.g., +234 for Nigeria)
                  </div>
                </div>

                {/* Financial Information - Only for Freelancers and Referrers */}
                {showFinancialSection && (
                  <>
                    <div className="section-divider">
                      <h3 className="text-lg font-semibold text-gray-900">Financial Information</h3>
                      {!hasFinancialInfo && !isEditing && (
                        <span className="required-badge">Required for payments</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="bank_name" className="form-label">Bank Name *</label>
                      <input
                        type="text"
                        id="bank_name"
                        name="bank_name"
                        value={profile.bank_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="e.g., First Bank, Zenith Bank"
                        required={showFinancialSection}
                        title="Bank Name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="account_name" className="form-label">Account Name *</label>
                      <input
                        type="text"
                        id="account_name"
                        name="account_name"
                        value={profile.account_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="Name as it appears on bank account"
                        required={showFinancialSection}
                        title="Account Name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="account_number" className="form-label">Account Number *</label>
                      <input
                        type="text"
                        id="account_number"
                        name="account_number"
                        value={profile.account_number}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="10-digit account number"
                        maxLength={10}
                        required={showFinancialSection}
                        title="Account Number"
                      />
                      <div className="form-hint">
                        10-digit bank account number
                      </div>
                    </div>
                  </>
                )}
              </div>

              {isEditing && (
                <div className="action-buttons">
                  <button 
                    type="button"
                    onClick={handleSaveProfile} 
                    className="btn btn-primary"
                    disabled={isSaving}
                    title="Save Changes"
                  >
                    {isSaving ? (
                      <>
                        <div className="loading-spinner mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      loadProfile(); // Reload original data
                    }} 
                    className="btn btn-secondary"
                    title="Cancel"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Security Card */}
            <div className="profile-card">
              <div className="card-header">
                <h2 className="card-title">Security</h2>
              </div>
              <div className="security-section">
                <div>
                  <p className="text-gray-600 mb-2">
                    Change your password to keep your account secure.
                  </p>
                  <p className="text-sm text-gray-500">
                    Password must be at least 6 characters long.
                  </p>
                </div>
                <button 
                  type="button"
                  onClick={handleChangePassword}
                  className="btn btn-secondary"
                  title="Change Password"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Change Password
                </button>
              </div>
            </div>

            {/* Profile Status Card */}
            <div className="profile-card">
              <div className="card-header">
                <h2 className="card-title">Profile Status</h2>
              </div>
              <div className="completion-status">
                <div className="status-item">
                  <span className={`status-icon ${profile.whatsapp_contact ? 'completed' : 'pending'}`}>
                    {profile.whatsapp_contact ? '✅' : '⭕'}
                  </span>
                  <span>WhatsApp Contact</span>
                </div>
                
                {showFinancialSection && (
                  <div className="status-item">
                    <span className={`status-icon ${hasFinancialInfo ? 'completed' : 'pending'}`}>
                      {hasFinancialInfo ? '✅' : '⭕'}
                    </span>
                    <span>Financial Information</span>
                  </div>
                )}
                
                <div className="status-item">
                  <span className={`status-icon ${profile.is_approved ? 'completed' : 'pending'}`}>
                    {profile.is_approved ? '✅' : '⭕'}
                  </span>
                  <span>Account Approval</span>
                </div>
              </div>
              
              {(!profile.whatsapp_contact || (showFinancialSection && !hasFinancialInfo)) && (
                <div className="completion-warning">
                  <p>⚠️ Complete all required fields to request account approval.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default Profile;